// Vertex union and exports
// ADR-0007: Hybrid File Organization Strategy
// ADR-0008: Type Field Dual Purpose

import { z } from 'zod';

// Import all vertex schemas
import { PressureBoundaryVertexSchema } from './pressure-boundary';
import { JunctionVertexSchema } from './junction';
import { FlowDemandVertexSchema } from './flow-demand';
import { CentrifugalPumpVertexSchema } from './centrifugal-pump';
import { ControlValveVertexSchema } from './control-valve';
import { TankVertexSchema } from './tank';
// v2 additions
import { BalancingOrificeVertexSchema } from './balancing-orifice';
import { FixedLossVertexSchema } from './fixed-loss';
import { ReducerVertexSchema } from './reducer';
import { CurveLossVertexSchema } from './curve-loss';
import { BlowerVertexSchema } from './blower';
import { FanVertexSchema } from './fan';
import { CompressorVertexSchema } from './compressor';
import { PressureGainVertexSchema } from './pressure-gain';
import { PressureReliefValveVertexSchema } from './pressure-relief-valve';
import { ThreeWayValveVertexSchema } from './three-way-valve';
import { NozzleMeterVertexSchema } from './nozzle-meter';
import { OrificeMeterVertexSchema } from './orifice-meter';
import { VenturiMeterVertexSchema } from './venturi-meter';
import { HeatSourceSinkVertexSchema } from './heat-source-sink';
import { HeatExchangerVertexSchema } from './heat-exchanger';
import { TeeVertexSchema } from './tee';
import { BendVertexSchema } from './bend';
import { StrainerVertexSchema } from './strainer';
import { CheckValveVertexSchema } from './check-valve';

/**
 * Vertex Schema - Discriminated Union (25 types)
 * ADR-0008: Type field serves dual purpose:
 * 1. TypeScript type narrowing (compile-time)
 * 2. React Flow component routing (runtime)
 */
export const VertexSchema = z.discriminatedUnion('type', [
  // v1 types
  PressureBoundaryVertexSchema,
  JunctionVertexSchema,
  FlowDemandVertexSchema,
  CentrifugalPumpVertexSchema,
  ControlValveVertexSchema,
  TankVertexSchema,
  // v2 types
  BalancingOrificeVertexSchema,
  FixedLossVertexSchema,
  ReducerVertexSchema,
  CurveLossVertexSchema,
  BlowerVertexSchema,
  FanVertexSchema,
  CompressorVertexSchema,
  PressureGainVertexSchema,
  PressureReliefValveVertexSchema,
  ThreeWayValveVertexSchema,
  NozzleMeterVertexSchema,
  OrificeMeterVertexSchema,
  VenturiMeterVertexSchema,
  HeatSourceSinkVertexSchema,
  HeatExchangerVertexSchema,
  // Fittings
  TeeVertexSchema,
  BendVertexSchema,
  // Connect-only
  StrainerVertexSchema,
  CheckValveVertexSchema,
]);

export type Vertex = z.infer<typeof VertexSchema>;

// Re-export specific vertex types for convenience
export type { PressureBoundaryVertex } from './pressure-boundary';
export type { JunctionVertex } from './junction';
export type { FlowDemandVertex, FlowDemandOperationConfig } from './flow-demand';
export type { CentrifugalPumpVertex, PumpOperationConfig } from './centrifugal-pump';
export type { ControlValveVertex, ValveOperationConfig } from './control-valve';
export type { TankVertex, TankPenetration, TankGeometry } from './tank';
// v2 type exports
export type { BalancingOrificeVertex } from './balancing-orifice';
export type { FixedLossVertex } from './fixed-loss';
export type { ReducerVertex } from './reducer';
export type { CurveLossVertex } from './curve-loss';
export type { BlowerVertex } from './blower';
export type { FanVertex } from './fan';
export type { CompressorVertex } from './compressor';
export type { PressureGainVertex } from './pressure-gain';
export type { PressureReliefValveVertex } from './pressure-relief-valve';
export type { ThreeWayValveVertex, ThreeWayValvePortOperation } from './three-way-valve';
export type { NozzleMeterVertex } from './nozzle-meter';
export type { OrificeMeterVertex } from './orifice-meter';
export type { VenturiMeterVertex } from './venturi-meter';
export type { HeatSourceSinkVertex } from './heat-source-sink';
export type { HeatExchangerVertex, HxConfiguration } from './heat-exchanger';
export type { TeeVertex } from './tee';
export type { BendVertex } from './bend';
export type { StrainerVertex } from './strainer';
export type { CheckValveVertex } from './check-valve';

// Re-export schemas
export { PressureBoundaryVertexSchema } from './pressure-boundary';
export { JunctionVertexSchema } from './junction';
export { FlowDemandVertexSchema, FlowDemandOperationConfigSchema } from './flow-demand';
export { CentrifugalPumpVertexSchema, PumpOperationConfigSchema } from './centrifugal-pump';
export { ControlValveVertexSchema, ValveOperationConfigSchema } from './control-valve';
export { TankVertexSchema, TankPenetrationSchema, TankGeometrySchema } from './tank';
// v2 schema exports
export { BalancingOrificeVertexSchema } from './balancing-orifice';
export { FixedLossVertexSchema } from './fixed-loss';
export { ReducerVertexSchema } from './reducer';
export { CurveLossVertexSchema } from './curve-loss';
export { BlowerVertexSchema } from './blower';
export { FanVertexSchema } from './fan';
export { CompressorVertexSchema } from './compressor';
export { PressureGainVertexSchema } from './pressure-gain';
export { PressureReliefValveVertexSchema } from './pressure-relief-valve';
export { ThreeWayValveVertexSchema, ThreeWayValvePortOperationSchema } from './three-way-valve';
export { NozzleMeterVertexSchema } from './nozzle-meter';
export { OrificeMeterVertexSchema } from './orifice-meter';
export { VenturiMeterVertexSchema } from './venturi-meter';
export { HeatSourceSinkVertexSchema } from './heat-source-sink';
export { HeatExchangerVertexSchema, HxConfigurationSchema } from './heat-exchanger';
export { TeeVertexSchema } from './tee';
export { BendVertexSchema } from './bend';
export { StrainerVertexSchema } from './strainer';
export { CheckValveVertexSchema } from './check-valve';
export { ValveDataSourceSchema } from './control-valve';
export type { ValveDataSource } from './control-valve';

export {
  liquidVolumeAtLevel,
  liquidLevelAtVolume,
  maximumLiquidLevel,
  tankCapacity,
} from './tank-geometry';
export {
  buildTankLevelTable,
  volumeAtLevelFromTable,
  levelAtVolumeFromTable,
} from './tank-level-table';
export type { TankLevelTable } from './tank-level-table';
